import * as signalR from "@microsoft/signalr";
import moment from "moment";
import { AppInsightsLogger } from "../components/shared/logging/AppInsightsLogger";
import IAuctionStatus from "../models/auction/IAuctionStatus";
import AuctionsHub, {
    IApprovedParcelBid,
    IBatchLiveUpdate,
    IBidderUpdate,
    IParcelBidLogUpdate,
    IParcelClocksUpdate,
    ILatencyTestResults,
    IBidProcessingComplete
} from "../models/hubs/AuctionsHub";
import { IParcelBid } from "../models/parcel/IParcelBid";
import Constants from "../models/utility/Constants";
import { IAppDispatch, IAppState } from "../store";
import * as AuctionStore from "../store/AuctionStore";
import TokenProvider from "./TokenProvider";
import { actionCreators as ParcelBidActions } from "../store/ParcelBidStore";
import {
    hideAllSnackbarMessages,
    setShowConnectedSnackbarMessage,
    setShowReconnectingSnackbarMessage,
    setShowRefreshSnackbarMessage
} from "../api/connectionSlice";
import * as ParcelBidStore from "../store/ParcelBidStore";
import {
    BidLoadState,
    isCompletedBidLoadState
} from "../models/utility/LoadState";

enum ConnectionAttemptState {
    None,
    ShouldConnect,
    ShouldDisconnect
}

export default class SignalRProvider {
    public static AuctionHubConnection: signalR.HubConnection;

    private static logMissedMessagesId: NodeJS.Timeout;
    private static currentConnectionAttempt = ConnectionAttemptState.None;

    public static BuildConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState,
        hubConnectionBuilder: signalR.HubConnectionBuilder = new signalR.HubConnectionBuilder()
    ): void {
        this.AuctionHubConnection = hubConnectionBuilder
            .withUrl("/hub/Auctions", {
                accessTokenFactory: () => TokenProvider.GetStoredToken() || ""
            })
            .withAutomaticReconnect()
            .configureLogging(
                process.env.NODE_ENV === Constants.NodeEnvironmentDev
                    ? Constants.SignalrLogLevelDev
                    : Constants.SignalrLogLevel
            )
            .build();

        this.AuctionHubConnection.onreconnecting(() => {
            setShowReconnectingSnackbarMessage(true);
            AppInsightsLogger.trackEvent({
                name: "SignalR is attempting to reconnect"
            });
        });

        this.AuctionHubConnection.onreconnected(() => {
            setShowConnectedSnackbarMessage(true);
            AppInsightsLogger.trackEvent({
                name: "SignalR successfully reconnected"
            });

            AuctionStore.actionCreators.getLatestAuctionInfo()(
                dispatch,
                getState
            );
        });

        this.AuctionHubConnection.onclose((err?: Error) => {
            AppInsightsLogger.trackEvent({
                name: `SignalR connection closed. ${
                    document.hidden
                        ? "Not visible; no action"
                        : "Visible; immediately reconnecting"
                }${err ? ` Error: ${err.message}.` : ""}`
            });
        });

        // register commands for all built connections
        this.registerReceiveCommands(dispatch, getState);
        this.StartAllConnections(dispatch, getState);
    }

    public static async ResetAllConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ): Promise<void> {
        await this.StopAllConnections(dispatch, getState);
        await this.StartAllConnections(dispatch, getState);
    }

    private static async StartAllConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ): Promise<void> {
        // If another attempt is already in flight, note attempt type & don't start connection
        const isAlreadyInProgress =
            this.currentConnectionAttempt !== ConnectionAttemptState.None;
        this.currentConnectionAttempt = ConnectionAttemptState.ShouldConnect;
        if (isAlreadyInProgress) {
            return;
        }

        // Otherwise, connect
        try {
            await this.AuctionHubConnection.start();
            AppInsightsLogger.trackEvent({
                name: `SignalR connection started.`
            });
            AuctionStore.actionCreators.getLatestAuctionInfo()(
                dispatch,
                getState
            );
            hideAllSnackbarMessages();
        } catch (err) {
            let msg = "";
            // parse error message
            if (err instanceof Error) {
                msg = err.message;
                if (!msg) {
                    msg = "Connection refused.";
                }
            } else if (typeof err === "string") {
                msg = err;
            } else {
                msg = JSON.stringify(err);
            }
            AppInsightsLogger.trackEvent({
                name: `Error: SignalR could not connect (${
                    document.hidden ? "Not visible" : "Visible"
                }): ${msg}`
            });
            if (!document.hidden) {
                setShowRefreshSnackbarMessage(true);
            }
        } finally {
            // After connection is started or fails, check if connection attempt type changed. Could've changed multiple times,
            // but we only care if disconnection attempted during connection & connection succeeded
            const disconnectAttemptedDuringConnection =
                this.isCurrentConnectionAttempt(
                    ConnectionAttemptState.ShouldDisconnect
                );
            this.currentConnectionAttempt = ConnectionAttemptState.None;
            if (
                disconnectAttemptedDuringConnection &&
                this.AuctionHubConnection.state ===
                    signalR.HubConnectionState.Connected
            ) {
                this.StopAllConnections(dispatch, getState);
            }
        }
    }

    private static isCurrentConnectionAttempt(
        stateToCheck: ConnectionAttemptState
    ): boolean {
        // Stephen Visser, Oct 27 -- prefer to do this inline but moved into method to address compile-time complaint
        return this.currentConnectionAttempt === stateToCheck;
    }

    private static async StopAllConnections(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ): Promise<void> {
        // If another attempt is already in flight, just note our attempt type & don't disconnect
        const isAlreadyInProgress =
            this.currentConnectionAttempt !== ConnectionAttemptState.None;
        this.currentConnectionAttempt = ConnectionAttemptState.ShouldDisconnect;
        if (isAlreadyInProgress) {
            return;
        }

        SignalRProvider.clearTimeoutForMissedMessages();

        // Otherwise disconnect
        try {
            await this.AuctionHubConnection.stop();
        } catch (err) {
            let msg = "";
            // parse error message
            if (err instanceof Error) {
                msg = err.message;
                if (!msg) {
                    msg = "Connection refused.";
                }
            } else if (typeof err === "string") {
                msg = err;
            } else {
                msg = JSON.stringify(err);
            }
            AppInsightsLogger.trackEvent({
                name: `Error: SignalR failed to disconnect. ${msg}`
            });
        } finally {
            // After disconnection is started, check if connection attempt type changed. Could've changed multiple times,
            // but we only care if connection attempt made during disconnection & disconnection succeeded.
            const connectAttemptedDuringDisconnection =
                this.isCurrentConnectionAttempt(
                    ConnectionAttemptState.ShouldConnect
                );
            this.currentConnectionAttempt = ConnectionAttemptState.None;
            if (
                connectAttemptedDuringDisconnection &&
                signalR.HubConnectionState.Disconnected
            ) {
                this.StartAllConnections(dispatch, getState);
            }
        }
    }

    private static setTimeoutForMissedMessages = (messageDueTime: string) => {
        SignalRProvider.clearTimeoutForMissedMessages();

        const timeout = moment(messageDueTime).diff(moment(), "milliseconds");
        SignalRProvider.logMissedMessagesId = setTimeout(
            SignalRProvider.LogMissedMessages,
            timeout
        );
    };

    private static clearTimeoutForMissedMessages = () => {
        clearTimeout(SignalRProvider.logMissedMessagesId);
    };

    private static LogMissedMessages = () => {
        AppInsightsLogger.trackEvent({
            name: "SignalR - ReceiveParcelClocksUpdate message missed"
        });
    };

    private static registerReceiveCommands(
        dispatch: IAppDispatch,
        getState: () => IAppState
    ) {
        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveBatchLiveStatus,
            (update: IBatchLiveUpdate) => {
                update.auctions.forEach((a) => {
                    dispatch({
                        type: "TOGGLE_AUCTION_LIVE_STATUS",
                        auctionId: a.auctionId,
                        live: a.live,
                        completed: a.completed
                    });
                });
                update.parcels.forEach((p) => {
                    dispatch({
                        endDate: p.endDate,
                        isCompleted: p.isCompleted,
                        isLive: p.isLive,
                        parcelId: p.parcelId,
                        startDate: p.startDate,
                        type: "TOGGLE_PARCEL_LIVE_STATUS"
                    });
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveApprovedParcelBid,
            (update: IApprovedParcelBid) => {
                const state = getState();
                const { parcelBidStoreState, userStoreState } = state;
                const { bidDialogVisible, currentBid } = parcelBidStoreState;

                // Close the confirm bid dialog if an update comes in while a user has it open to reduce outbid errors
                if (
                    bidDialogVisible &&
                    currentBid.submitLoadState === BidLoadState.NotSubmitted &&
                    update.parcelId === currentBid.parcelId &&
                    update.userId !== userStoreState.user?.id
                ) {
                    ParcelBidStore.actionCreators.resetCurrentBid()(
                        dispatch,
                        getState
                    );
                    ParcelBidStore.actionCreators.toggleDialogVisible(false)(
                        dispatch,
                        getState
                    );
                }

                dispatch({
                    type: "UPDATE_PARCEL_BID_AMOUNT",
                    amount: update.bidAmount,
                    parcelId: update.parcelId,
                    tokenId: update.checkoutToken,
                    endDate: update.endTime
                });

                const bid = {
                    bidAmount: update.bidAmount,
                    bidReceivedTime: update.bidReceivedTime,
                    parcelId: update.parcelId,
                    userId: update.userId
                } as IParcelBid;
                dispatch({ type: "ADD_PARCEL_BID", bid });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveParcelBidLogUpdate,
            (update: IParcelBidLogUpdate) => {
                dispatch({
                    type: "ADD_PARCEL_ADMIN_BID_LOG",
                    parcelId: update.parcelId,
                    bidLog: update.bidLog
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveParcelClocksUpdate,
            (update: IParcelClocksUpdate) => {
                SignalRProvider.setTimeoutForMissedMessages(
                    update.nextMessageDueTime
                );

                let connectionShouldReset = false;

                const resetConnection = () => {
                    connectionShouldReset = true;
                };

                dispatch({
                    type: "CHECKPOINT_LIVE_PARCELS",
                    liveParcels: update.liveParcels,
                    resetConnection
                });

                if (connectionShouldReset) {
                    AppInsightsLogger.trackEvent({
                        name: "Client out of sync, reconnecting..."
                    });
                    console.log("Client out of sync, reconnecting...");
                    SignalRProvider.ResetAllConnections(dispatch, getState);
                }

                const offset = moment(new Date(update.currentServerTime)).diff(
                    moment(new Date())
                );
                dispatch({ type: "SET_CLIENT_TIMER_OFFSET", offset });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveParcelUpdate,
            (update: IAuctionStatus) => {
                dispatch({
                    type: "UPDATE_LIVE_PARCEL_INFORMATION",
                    auctionStatus: update
                });
                if (update.updateMessage) {
                    dispatch({
                        type: "SHOW_PARCEL_UPDATED_SNACKBAR_MESSAGE",
                        auctionId: update.auctionId,
                        message: update.updateMessage
                    });
                    dispatch({ type: "HIDE_PARCEL_UPDATED_SNACKBAR_MESSAGE" });
                }
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.ReceiveApprovedEnBlocBidders,
            (update: IBidderUpdate) => {
                dispatch({
                    parcelId: update.parcelId,
                    approvedEnBlocBidders: update.bidders,
                    type: "UPDATE_PARCEL_BIDDERS"
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.DebugCommands.ReceiveLatencyTestResults,
            (results: ILatencyTestResults) => {
                dispatch({
                    testResults: results,
                    type: "RECEIVE_LATENCY_TEST_RESULTS"
                });
            }
        );

        this.AuctionHubConnection.on(
            AuctionsHub.ReceiveCommands.BidProcessingComplete,
            (bidResult: IBidProcessingComplete) => {
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText: bidResult.errorText,
                    state: bidResult.result
                });

                if (isCompletedBidLoadState(bidResult.result)) {
                    dispatch({
                        type: "SENTRY_STOP_PROCESS_BID"
                    });
                }

                ParcelBidActions.updateCurrentBid(
                    bidResult.parcelId,
                    bidResult.result,
                    bidResult.checkoutToken,
                    bidResult.lastAcceptedBid,
                    bidResult.errorText
                )(dispatch, getState);
            }
        );
    }
}
